"use client";
import type { RowData } from "@tanstack/react-table";

import { lazyComponent } from "@/components/ui/lazy-component";
import { LoadFailureNotice } from "@/components/ui/load-failure-notice";
import { Button } from "@/components/ui/button";
import { RecordPresentationSwitcher } from "./record-presentation-switcher";
import { selectedRowIds } from "./record-table-model";
import type { RecordTableModel } from "./use-record-table-model";
import type { RecordTableProps } from "./types";

const RecordControlBar = lazyComponent(
  () =>
    import("./record-control-bar").then((module) => module.RecordControlBar),
  {
    loading: null,
    failure: {
      label: "record-control-bar",
      fallback: (
        <LoadFailureNotice title="The table controls could not load." />
      ),
    },
  },
);

interface RecordTableToolbarProps<TData extends RowData> {
  props: RecordTableProps<TData>;
  model: RecordTableModel<TData>;
  onOpenColumns: () => void;
  onSaveView: () => void;
}

function RecordSelectionBar({
  selectedIds,
  bulkActions,
  setSelection,
  clearSelection,
}: {
  selectedIds: string[];
  bulkActions: NonNullable<RecordTableProps<RowData>["bulkActions"]>;
  setSelection: (ids: string[]) => void;
  clearSelection: () => void;
}) {
  return (
    <div className="flex min-h-[52px] shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b border-border bg-accent px-3 py-2">
      <span className="text-data font-medium">
        {selectedIds.length} selected
      </span>
      <div className="flex items-center gap-2">
        {bulkActions(selectedIds, clearSelection, setSelection)}
      </div>
      <Button
        variant="ghost"
        size="compact"
        className="ml-auto"
        onClick={clearSelection}
      >
        Clear
      </Button>
    </div>
  );
}

function phoneViewsFor<TData extends RowData>(
  { object, state }: RecordTableProps<TData>,
  model: RecordTableModel<TData>,
) {
  if (!object.capabilities.views) return undefined;
  return { views: model.allViews, activeViewId: model.activeViewId, onChange: state.applyView };
}

export function RecordTableToolbar<TData extends RowData>({
  props,
  model,
  onOpenColumns,
  onSaveView,
}: RecordTableToolbarProps<TData>) {
  const { object, state, bulkActions } = props;
  const { presentation } = model;
  const selectedIds = selectedRowIds(state.rowSelection);
  if (model.selectable && selectedIds.length > 0 && bulkActions) {
    return (
      <RecordSelectionBar
        selectedIds={selectedIds}
        bulkActions={bulkActions}
        clearSelection={() => state.setRowSelection({})}
        setSelection={(ids) =>
          state.setRowSelection(Object.fromEntries(ids.map((id) => [id, true])))
        }
      />
    );
  }
  if (!model.showControls) return null;
  return (
    <RecordControlBar
      object={object}
      state={state}
      onOpenColumns={onOpenColumns}
      onSaveView={
        model.canPersistViews && state.isDirty ? onSaveView : undefined
      }
      phoneViews={phoneViewsFor(props, model)}
      trailing={
        <>
          {props.controlActions}
          {presentation.showSwitcher ? (
            <RecordPresentationSwitcher
              modes={presentation.modes}
              value={presentation.mode}
              onChange={state.setPresentationMode}
            />
          ) : null}
        </>
      }
    />
  );
}
